import { CommonModule } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { UntypedFormControl, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyAutocompleteModule as MatAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import { Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  skip,
  startWith,
  takeUntil,
} from 'rxjs/operators';

import { SnackBarConfig } from '../../../shared/configs';
import { trackById } from '../../../shared/util';

import { LocationService } from './../../../../../app/core/locations/services/location.service';

interface ILocation {
  id :number;
  location_name :string;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatInputModule,
    MatAutocompleteModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
  ],
  selector: 'lum-ag-grid-search-locations',
  templateUrl: './ag-grid-search-locations.component.html',
  styleUrls: ['./ag-grid-search-locations.component.scss'],
})
export class AgGridSearchLocationsComponent
  implements ICellEditorAngularComp, OnDestroy
{
  searchControl = new UntypedFormControl();
  selectedItem :ILocation | null = null;
  previousSelectedItem :ILocation | null = null;
  loadingLocations :boolean = false;
  locations :ILocation[] = [];

  trackById = trackById;

  private params :ICellEditorParams;
  private onDestroy$ = new Subject<void>();

  constructor(
    private snackBar :MatSnackBar,
    private locationService :LocationService,
  ) {}

  agInit(params :ICellEditorParams) :void {
    this.params = params;
    this.selectedItem = params.value;
    this.previousSelectedItem = this.selectedItem;
    this.initSearchControlListener();
    this.searchLocations();
  }

  getValue() :ILocation | null {
    return this.selectedItem;
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  selectValue(item :ILocation) :void {
    this.selectedItem = item;
    this.updateLocationMapping();
  }

  displaySelectedItem() :string {
    return this.formattedSelectedItem;
  }

  removeSelectedItem() :void {
    this.selectedItem = null;
    this.updateLocationMapping();
  }

  private updateLocationMapping() :void {
    // Implement the logic to update the location mapping
    // This might involve calling an API or updating the grid data
    this.params.api.stopEditing();
    this.params.api.refreshCells({
      rowNodes: [this.params.node],
      columns: [this.params.column],
      force: true,
    });
  }

  private initSearchControlListener() :void {
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        startWith(''),
        skip(1),
        takeUntil(this.onDestroy$),
      )
      .subscribe((keyword :string) => {
        this.searchLocations(keyword);
      });
  }

  private searchLocations(keyword ?:string) :void {
    this.loadingLocations = true;
    this.locationService
      .getAsync({
        per_page: '10',
        page: '1',
        keyword: keyword || '',
        limit: '100',
        parentWarehouseGroupId: this.params.data?.warehouseGroupId?.toString(),
      })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.loadingLocations = false;
        }),
      )
      .subscribe({
        next: (response :any) => {
          this.locations = response.data || [];
        },
        error: (error :any) => {
          this.snackBar.open('Error when searching locations.', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  get formattedSelectedItem() :string {
    return this.selectedItem?.location_name || '';
  }
}
